/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, October 4th 2021, 11:26:55 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { invariant } from "immutability-helper";
import validator from "validator";
import { logger } from "lib/logger";
import {
  APISearchParameters,
  APIOrderingParameters,
  SortDirection,
  ObjectKey,
  AllowedModelFieldSearchKey,
} from "lib/types";

/**
 *
 * Sorting Helper Functions
 *
 */
export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator<Key extends keyof any>(
  order: SortDirection,
  orderBy: any
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
export function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/**
 *
 * Helper Functions for LISTS API based on DJango Rest Framework used by OneStop API
 *
 */

interface buildAPIOrderingParamsTypes<T> {
  apiOrder: SortDirection;
  apiOrderBy: ObjectKey<T>;
}

export const buildAPIOrderingParams = <T>({ apiOrder, apiOrderBy }: buildAPIOrderingParamsTypes<T>) => {
  let apiOrderParams: APIOrderingParameters<T> = {};
  apiOrderParams.ordering = apiOrder === "desc" ? `-${apiOrderBy}` : apiOrderBy;
  return apiOrderParams;
};

interface buildAPISearchParamsTypes<T> {
  apiSearchKeys?: AllowedModelFieldSearchKey<T>[];
  apiSearchText: string;
}

/**
 *
 * apiSearchKeys This is currently not used until in favour of generic search text in API
 * and because when you provided multiple search keys, Django filters does an AND instead of OR.
 * Thus our use of generic search text.
 *
 * @param apiSearchKeys The search keys to use, if any. If none is provided, we send generic key 'search_text'
 * to API with searchText value entered by user.
 * @param apiSearchText The search text to pass to the api
 * @returns
 */

export const buildAPISearchParams = <T>({ apiSearchKeys = [], apiSearchText }: buildAPISearchParamsTypes<T>) => {
  let searchParams: APISearchParameters<T> = {};
  if (apiSearchKeys.length > 0) {
    apiSearchKeys.forEach((key) => {
      searchParams[key] = apiSearchText;
    });
  } else {
    return { search_text: apiSearchText, search: apiSearchText };
  }

  return searchParams;
};

// Convert bytes to readable size format
// https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

interface FilterObjectArrayArgs<T> {
  objectArray: T[];
  searchKeys?: ObjectKey<T>[];
  getObjectValue?: (item: T) => string;
  searchText: string;
}

export const filterObjectArray = <T>(args: FilterObjectArrayArgs<T>) => {
  const { objectArray, searchKeys = [], getObjectValue, searchText } = args;

  if (!searchText) return objectArray;

  return objectArray.filter((item) => {
    if (getObjectValue) {
      return containsSearchText(getObjectValue(item), searchText);
    }

    return Boolean(searchForKeys<T>(item, searchKeys, searchText));
  });
};

const searchForKeys = <T>(object: T, searchKeys: ObjectKey<T>[], searchText: string) => {
  return searchKeys?.some((objectKey) => {
    // Getting nested object values
    const objectValue = objectKey
      .toString()
      .split(".")
      .reduce((prev: any, curr: any) => prev[curr], object);

    if (!objectValue) return false;

    return containsSearchText(objectValue.toString(), searchText);
  });
};

export const containsSearchText = (candidate: string, searchText: string) => {
  return candidate.toLowerCase().replace(/\s+/g, "").includes(searchText.toLowerCase().replace(/\s+/g, ""));
};

export const getCookie = (name) => {
  let cookieValue = null;
  document.cookie?.split(";").every((ck) => {
    const cookie = ck.trim();
    if (cookie.substring(0, name.length + 1) === name + "=") {
      cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
      return false; //Break
    }
    return true;
  });

  return cookieValue;
};

// Got this from https://stackoverflow.com/questions/9461621/format-a-number-as-2-5k-if-a-thousand-or-more-otherwise-900
export const numberToString = (num: number, digits?: number) => {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];

  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;

  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
};

export const convertTextToInitials = (text: string): string[] =>
  text
    .split(" ")
    .filter((v) => v !== "")
    .map((v) => v[0].toUpperCase());

export const getRandomWholeNumber = (maximumDigits?: number) =>
  Math.ceil(Math.random() * Math.pow(10, maximumDigits ?? 2));

export const parsePrimaryKeyValue = (primaryKedId: string | number): string | number => {
  // In OneStop, we only support integers and UUIDs as primary keys for now.
  invariant(
    typeof primaryKedId === "number" ||
      (typeof primaryKedId === "string" && (validator.isNumeric(primaryKedId) || validator.isUUID(primaryKedId))),
    () => "A primary key value must either be an integer or UUID."
  );

  if (typeof primaryKedId === "string") {
    if (validator.isNumeric(primaryKedId)) return parseInt(primaryKedId);
    if (validator.isUUID(primaryKedId)) return primaryKedId;
  }

  if (typeof primaryKedId === "number") return primaryKedId;
};

export const getBrowserObjectStorageFunctions = <T>() => {
  const saveObjectToBrowserStorage = async (key: string, object: T) => {
    try {
      localStorage.setItem(key, JSON.stringify(object));
    } catch (e) {
      logger.error("Error occurred while attempting to save data to browser storage", e);
    }
  };

  const getObjectFromBrowserStorage = (key: string): T => {
    try {
      return localStorage.getItem(key) ? JSON.parse(localStorage.getItem(key)) : undefined;
    } catch (e) {
      logger.error("Error occurred while attempting to retreive data from browser storage", e);
      return undefined;
    }
  };

  return { saveObjectToBrowserStorage, getObjectFromBrowserStorage };
};

export const isComplexType = (value: any) => typeof value === "function" || typeof value === "object";

export const isPrimitiveType = (value: any) => !isComplexType;

export const numberToHex = (number: number) => number.toString(16);

export const hexToNumber = (hexString: string) => parseInt(hexString, 16);

export const converHexColorToRGBTriplets = (hexColor: string): number[] => {
  let color = hexColor;

  if (color.startsWith("#")) color = color.substring(1);

  const hexStrings = color.match(/.{1,2}/g);

  return hexStrings.map((hexString) => hexToNumber(hexString));
};

const componentToHex = (c: string | number) => {
  var hex = c.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
};

export const convertRGBToHex = (r: string | number, g: string | number, b: string | number) => {
  return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
};

export function slugify(str: string, separator = "_") {
  str = str.replace(/^\s+|\s+$/g, ""); // trim
  str = str.toLowerCase();

  // remove accents, swap ñ for n, etc
  var from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;";
  var to = "aaaaeeeeiiiioooouuuunc------";
  for (var i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  str = str
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, separator) // collapse whitespace and replace by -
    .replace(/-+/g, separator); // collapse dashes

  return str;
}

/**
 * Checks if the string is a valid UUID string and also checks for 32 character UUIDs that only
 * have the hexadecimal values i.e. hyphens omitted, which is still a valid represenation.
 *
 * @param uuidString The UUID string to be tested
 * @returns Boolean
 *
 * TODO: Probably can be done more elegantly using a regex.
 *
 */
export const isValidUUID = (uuidString: string) => {
  if (!(typeof uuidString === "string")) return false;

  const isValidWithHyphens = validator.isUUID(uuidString);

  if (!isValidWithHyphens) {
    return (
      uuidString.length === 32 &&
      validator.isUUID(
        uuidString.substring(0, 8) +
          "-" +
          uuidString.substring(8, 12) +
          "-" +
          uuidString.substring(12, 16) +
          "-" +
          uuidString.substring(16, 20) +
          "-" +
          uuidString.substring(20, 32)
      )
    );
  }

  return isValidWithHyphens;
};

export const concatPath = (pathToAppendTo: string, pathToAppend: string) => {
  if (!pathToAppendTo.endsWith("/")) return `${pathToAppendTo}/${pathToAppend}`;

  return `${pathToAppendTo}${pathToAppend}`;
};

export function replaceKeyPreservePostion(obj: { [key: string]: any }, oldKey: string, newKey: string) {
  const newObj = {};

  // Iterate over all properties in the object
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      if (key === oldKey) {
        // Insert the new key in place of the old key
        newObj[newKey] = obj[key];
      } else {
        // Copy other properties as is
        newObj[key] = obj[key];
      }
    }
  }

  return newObj;
}

export const nth = (n: number) => ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || "th";

export const ordinal = (n: number) => `${n}${nth(n)}`;
